import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Container,
  Avatar,
  IconButton,
  Badge,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popover,
  Tooltip,
  Divider,
  Chip,
  Fab,
  InputAdornment,
  Card,
  CardHeader,
  CardContent,
  alpha,
  Slide,
  Fade,
  Switch,
  FormControlLabel,
  FormGroup,
  Menu,
  MenuItem,
  PaletteMode,
  CssBaseline,
  ThemeProvider,
  createTheme,
  CircularProgress,

} from '@mui/material';
import {
  Send as SendIcon,
  Search as SearchIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  Add as AddIcon,
  Mood as MoodIcon,
  AttachFile as AttachFileIcon,
  Mic as MicIcon,
  SmartToy as BotIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Palette as PaletteIcon,
  Check as CheckIcon,

} from '@mui/icons-material';
import { green, blue, grey, orange, purple, deepPurple, teal, cyan, red, pink, amber, indigo, deepOrange, lightBlue, lime } from '@mui/material/colors';
import axios from 'axios';
import echo from './echo';

// Theme configuration
const getDesignTokens = (mode, primaryColor = blue, secondaryColor = purple) => ({
  palette: {
    mode,
    primary: {
      main: primaryColor[500],
      light: primaryColor[300],
      dark: primaryColor[700],
    },
    secondary: {
      main: secondaryColor[500],
      light: secondaryColor[300],
      dark: secondaryColor[700],
    },
    background: {
      default: mode === 'light' ? '#f5f7fa' : '#121212',
      paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
    },
    text: {
      primary: mode === 'light' ? '#2c3e50' : '#ffffff',
      secondary: mode === 'light' ? '#546e7a' : '#b0b0b0',
    },
    gradient: {
      primary: mode === 'light'
        ? `linear-gradient(135deg, ${primaryColor[400]}, ${primaryColor[600]})`
        : `linear-gradient(135deg, ${primaryColor[600]}, ${primaryColor[800]})`,
      secondary: mode === 'light'
        ? `linear-gradient(135deg, ${secondaryColor[400]}, ${secondaryColor[600]})`
        : `linear-gradient(135deg, ${secondaryColor[600]}, ${secondaryColor[800]})`,
      sidebar: mode === 'light'
        ? 'linear-gradient(180deg, #2c3e50 0%, #3498db 100%)'
        : 'linear-gradient(180deg, #1a1a1a 0%, #2d2d2d 100%)',
      message: mode === 'light'
        ? `linear-gradient(135deg, ${primaryColor[500]}, ${secondaryColor[500]})`
        : `linear-gradient(135deg, ${primaryColor[600]}, ${secondaryColor[600]})`,
    }
  },
  shape: {
    borderRadius: 12,
  },
  shadows: mode === 'light'
    ? [
      "none",
      "0px 2px 4px rgba(0,0,0,0.1)",
      "0px 4px 8px rgba(0,0,0,0.1)",
      "0px 8px 16px rgba(0,0,0,0.1)",
      "0px 16px 24px rgba(0,0,0,0.1)",
      ...Array(20).fill("none")
    ]
    : [
      "none",
      "0px 2px 4px rgba(0,0,0,0.3)",
      "0px 4px 8px rgba(0,0,0,0.3)",
      "0px 8px 16px rgba(0,0,0,0.3)",
      "0px 16px 24px rgba(0,0,0,0.3)",
      ...Array(20).fill("none")
    ],
});

// Available color themes
const colorThemes = {
  blue: { primary: blue, secondary: purple },
  teal: { primary: teal, secondary: cyan },
  purple: { primary: purple, secondary: deepPurple },
  orange: { primary: orange, secondary: deepOrange },
  green: { primary: green, secondary: lime },
  pink: { primary: pink, secondary: red },
  indigo: { primary: indigo, secondary: lightBlue },
  amber: { primary: amber, secondary: orange },
};

const ParticipantSingleEventDashboard = () => {
  const eventid = localStorage.getItem('eventid');
  const eventName = localStorage.getItem('eventname');
  const participantName = localStorage.getItem('auth_name') || 'You';
  const participantRole = localStorage.getItem('auth_role') || 'Participant';
  const [isSending, setIsSending] = useState(false);

  // Theme state management
  const [mode, setMode] = useState(localStorage.getItem('chat-theme-mode') || 'light');
  const [colorTheme, setColorTheme] = useState(localStorage.getItem('chat-color-theme') || 'blue');
  const [themeAnchorEl, setThemeAnchorEl] = useState(null);

  const muiTheme = useMemo(() =>
    createTheme(getDesignTokens(
      mode,
      colorThemes[colorTheme].primary,
      colorThemes[colorTheme].secondary
    )),
    [mode, colorTheme]
  );

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // States
  const [showUserList, setShowUserList] = useState(!isMobile);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeChatUser, setActiveChatUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('All');
  const [sendToGroup, setSendToGroup] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [users, setUsers] = useState([]);
  const [logedInUser, setLogedInUser] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [groupUnreadCounts, setGroupUnreadCounts] = useState({});
  const [participantId, setParticipantId] = useState();
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');

  // Add optimistic messages state
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  const chatEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageTone = new Audio("https://actions.google.com/sounds/v1/alarms/beep_short.ogg");
  messageTone.preload = "auto";

  // Combine real messages with optimistic messages for display
  const allDisplayedMessages = useMemo(() => {
    const currentUser = parseInt(logedInUser?.id || participantId);

    // Filter both real and optimistic messages
    const filteredRealMessages = messages.filter((msg) => {
      if (sendToGroup) {
        return msg.group === selectedRole;
      }
      if (!activeChatUser) return false;
      const chatUser = parseInt(activeChatUser.id);
      return (
        (msg.sender_id === currentUser && msg.receiver_id === chatUser) ||
        (msg.sender_id === chatUser && msg.receiver_id === currentUser)
      );
    });

    const filteredOptimisticMessages = optimisticMessages.filter((msg) => {
      if (sendToGroup) {
        return msg.group === selectedRole;
      }
      if (!activeChatUser) return false;
      const chatUser = parseInt(activeChatUser.id);
      return (
        (msg.sender_id === currentUser && msg.receiver_id === chatUser) ||
        (msg.sender_id === chatUser && msg.receiver_id === currentUser)
      );
    });

    // Combine and sort by datetime
    return [...filteredRealMessages, ...filteredOptimisticMessages]
      .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
  }, [messages, optimisticMessages, sendToGroup, selectedRole, activeChatUser, logedInUser, participantId]);

  // Theme handlers
  const handleThemeToggle = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('chat-theme-mode', newMode);
  };

  const handleColorThemeOpen = (event) => {
    setThemeAnchorEl(event.currentTarget);
  };

  const handleColorThemeClose = () => {
    setThemeAnchorEl(null);
  };

  const handleColorThemeChange = (newColorTheme) => {
    setColorTheme(newColorTheme);
    localStorage.setItem('chat-color-theme', newColorTheme);
    handleColorThemeClose();
  };

  // Color themes based on role
  const getRoleColor = (role) => {
    const colors = {
      Organizer: deepPurple[500],
      Speaker: orange[500],
      Participant: muiTheme.palette.primary.main,
      All: teal[500],
      default: cyan[500]
    };
    return colors[role] || colors.default;
  };

  const getRoleGradient = (role) => {
    const gradients = {
      Organizer: `linear-gradient(135deg, ${deepPurple[400]}, ${deepPurple[600]})`,
      Speaker: `linear-gradient(135deg, ${orange[400]}, ${orange[600]})`,
      Participant: muiTheme.palette.gradient.primary,
      All: `linear-gradient(135deg, ${teal[400]}, ${teal[600]})`,
      default: `linear-gradient(135deg, ${cyan[400]}, ${cyan[600]})`
    };
    return gradients[role] || gradients.default;
  };

  // Pusher connection status
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!eventid || !token) {
    //  console.error("Missing event ID or auth token");
      setConnectionStatus("Cannot connect ❌");
      return;
    }

    const handleConnected = () => {
    //  console.log('Pusher connected');
      setConnectionStatus('Connected ✅');
    };

    const handleDisconnected = () => {
     // console.log('Pusher disconnected');
      setConnectionStatus('Disconnected ❌');
    };

    const handleError = (error) => {
    //  console.error('Pusher error:', error);
      setConnectionStatus('Error ❌');
    };

    echo.connector.pusher.connection.bind('connected', handleConnected);
    echo.connector.pusher.connection.bind('disconnected', handleDisconnected);
    echo.connector.pusher.connection.bind('error', handleError);

    return () => {
      echo.connector.pusher.connection.unbind('connected', handleConnected);
      echo.connector.pusher.connection.unbind('disconnected', handleDisconnected);
      echo.connector.pusher.connection.unbind('error', handleError);
    };
  }, [eventid]);

  // Laravel Echo/Pusher event listeners
  useEffect(() => {
    const currentUserId = parseInt(logedInUser?.id || participantId);
    if (!eventid || !currentUserId) return;

    // Listen for PRIVATE messages on your personal channel
    const privateChannel = echo.channel(`conference-chat.user.${currentUserId}`);

    privateChannel.subscribed(() => {
      setConnectionStatus('Connected & Subscribed ✅');
    });

    privateChannel.error((error) => {
      setConnectionStatus('Subscription Failed ❌');
    });

    privateChannel.listen('.ConferenceChatMessage', (e) => {
      handleNewMessage(e.message);
      if (e.counts) {
        setUnreadCounts(e.counts);
      }
    });

    // Listen for GROUP messages on the event channel
    const groupChannel = echo.channel(`conference-chat-group.${eventid}`);

    groupChannel.subscribed(() => {
    //  console.log(`✅ Subscribed to group channel: conference-chat-group.${eventid}`);
    });

    groupChannel.listen(".NewConferenceGroupMessage", (e) => {
      handleNewMessage(e.message);

      if (e.counts) {
        setGroupUnreadCounts(e.counts);
      }
    });

    return () => {
      privateChannel.stopListening(".ConferenceChatMessage");
      groupChannel.stopListening(".NewConferenceGroupMessage");
      echo.leave(`conference-chat.user.${currentUserId}`);
      echo.leave(`conference-chat-group.${eventid}`);
    };
  }, [eventid, logedInUser, participantId, selectedRole]);

  // Enhanced user fetching
  useEffect(() => {
    if (!eventid) return;

    const fetchUsersWithStatus = async () => {
      try {
        const response = await axios.get(`/api/conference-paticipant-chat/${eventid}`);
        if (response.data.status === 200) {
          const usersWithStatus = (response.data.cpindex || []).map(user => ({
            ...user,
            online: false
          }));
          setUsers(usersWithStatus);
          setParticipantId(response?.data?.participantId || 0);
          setLogedInUser(response.data.logedinUser);
          fetchOnlineStatus(usersWithStatus);
        }
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };

    const fetchOnlineStatus = async (usersList) => {
      try {
        const response = await axios.get(`/api/conference-online-users?event_id=${eventid}`);
        if (response.data.status === 200) {
          const onlineUsers = response.data.online_users || [];
          setUsers(prevUsers =>
            prevUsers.map(user => ({
              ...user,
              online: onlineUsers.includes(parseInt(user.id))
            }))
          );
        }
      } catch (error) {
        console.error('Failed to fetch online status:', error);
        setUsers(prevUsers =>
          prevUsers.map(user => ({
            ...user,
            online: false
          }))
        );
      }
    };

    fetchUsersWithStatus();
  }, [eventid]);

  // Online status management
  useEffect(() => {
    if (!logedInUser?.id || !eventid) return;

    const sendOnlineStatus = async (online) => {
      try {
        await axios.post('/api/conference-user-online', {
          event_id: eventid,
          user_id: logedInUser.id,
          online: online ? 1 : 0,
        });
      } catch (error) {
        console.error('Failed to update online status:', error);
      }
    };

    sendOnlineStatus(true);
    return () => {
      sendOnlineStatus(false);
    };
  }, [eventid, logedInUser]);

  // Handle online status updates from Pusher
  const handleUserOnlineStatus = useCallback((data) => {
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === data.user_id
          ? { ...user, online: data.online }
          : user
      )
    );

    if (activeChatUser && activeChatUser.id === data.user_id) {
      setActiveChatUser(prev => prev ? { ...prev, online: data.online } : null);
    }
  }, [activeChatUser]);

  // Handle new message from WebSocket - UPDATED FOR OPTIMISTIC MESSAGES
  const handleNewMessage = useCallback((messageData) => {
    const currentUserId = parseInt(logedInUser?.id || participantId);
    const isMine = parseInt(messageData.sender_id) === currentUserId;

    // console.log('🟢 Processing message:', {
    //   messageId: messageData.id,
    //   currentUserId,
    //   isMine,
    //   isGroup: messageData.group,
    //   senderId: messageData.sender_id,
    //   receiverId: messageData.receiver_id
    // });

    // If this is my message that was sent optimistically, remove it from optimistic state
    if (isMine) {
      setOptimisticMessages(prev =>
        prev.filter(msg =>
          !msg.isOptimistic ||
          msg.text !== messageData.text ||
          Math.abs(new Date(msg.datetime) - new Date(messageData.datetime)) > 5000
        )
      );
    }

    // Check if message already exists to prevent duplicates
    setMessages(prev => {
      if (prev.some(msg => msg.id === messageData.id)) {
       // console.log('🚫 Message already exists, skipping:', messageData.id);
        return prev;
      }

      const formattedMessage = {
        id: messageData.id,
        text: messageData.text || messageData.message,
        datetime: messageData.datetime,
        displayTime: formatTime(messageData.datetime),
        sender_id: parseInt(messageData.sender_id),
        receiver_id: parseInt(messageData.receiver_id),
        group: messageData.group,
        isMine,
        is_read: messageData.is_read || 0,
        sender_name: messageData.sender_name,
        sender_avatar: messageData.sender_name?.charAt(0),
        sender_role: messageData.sender_role,
      };

      //console.log('✅ Adding new message to state:', formattedMessage.id);
      return [...prev, formattedMessage];
    });

    // Handle GROUP messages
    if (messageData.group) {
     // console.log('🟡 Group message processing for group:', messageData.group);

      if (!isMine) {
        const isForCurrentGroup = messageData.group === selectedRole && sendToGroup;

        // console.log('🔍 Group message conditions:', {
        //   isForCurrentGroup,
        //   selectedRole,
        //   sendToGroup,
        //   messageGroup: messageData.group
        // });

        // Play tone for group messages in the current group
        if (isForCurrentGroup) {
          try {
            messageTone.currentTime = 0;
            messageTone.play().catch((err) => {
            //  console.warn("Autoplay blocked:", err);
            });
          } catch (e) {
            console.error("Error playing message tone:", e);
          }
        }

        // Increment unread count for this group if not currently viewing it
        if (!isForCurrentGroup) {
         // console.log('📈 Incrementing unread count for group:', messageData.group);
          setGroupUnreadCounts(prev => ({
            ...prev,
            [messageData.group]: (prev[messageData.group] || 0) + 1
          }));
        } else {
          // If viewing the group, mark as read immediately
          //console.log('📖 Marking group message as read');
          markMessageAsRead(messageData.id, currentUserId, true, messageData.group);
        }
      }
    }
    // Handle PRIVATE messages (only if not a group message)
    else if (!messageData.group) {
    //  console.log('🔵 Private message processing');
      const isForMe = parseInt(messageData.receiver_id) === currentUserId;
      const isFromMe = parseInt(messageData.sender_id) === currentUserId;

      // console.log('🔍 Private message details:', {
      //   isForMe,
      //   isFromMe,
      //   receiverId: messageData.receiver_id,
      //   currentUserId
      // });

      // If message is for me and I didn't send it
      if (isForMe && !isFromMe) {
      //  console.log('🎯 Private message addressed to me');

        const isFromActiveChat = activeChatUser &&
          parseInt(activeChatUser.id) === parseInt(messageData.sender_id);

        // console.log('💬 Active chat check:', {
        //   isFromActiveChat,
        //   activeChatUserId: activeChatUser?.id,
        //   senderId: messageData.sender_id
        // });

        // Play sound for new private messages
        try {
          messageTone.currentTime = 0;
          messageTone.play().catch((err) => {
           // console.warn("Autoplay blocked:", err);
          });
        } catch (e) {
          console.error("Error playing message tone:", e);
        }

        if (!isFromActiveChat) {
          // Increment unread count if not from active chat
         // console.log('📈 Incrementing unread count for sender:', messageData.sender_id);
          setUnreadCounts(prev => ({
            ...prev,
            [messageData.sender_id]: (prev[messageData.sender_id] || 0) + 1
          }));
        } else {
          // Mark as read immediately if from active chat
         // console.log('📖 Marking private message as read (active chat)');
          markMessageAsRead(messageData.id, currentUserId, false, null, messageData.sender_id);
        }
      }

      // If I sent the message to someone I'm actively chatting with, mark it as read immediately
      if (isFromMe && activeChatUser) {
        const isToActiveChat = parseInt(activeChatUser.id) === parseInt(messageData.receiver_id);

        if (isToActiveChat) {
         // console.log('📖 Marking my own message as read (active chat)');
          markMessageAsRead(messageData.id, currentUserId, false, null, messageData.receiver_id);
        }
      }
    }

    // Scroll to bottom after a short delay
    setTimeout(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, [logedInUser, participantId, activeChatUser, sendToGroup, selectedRole, eventid]);

  // Separate function for marking messages as read to avoid code duplication
  const markMessageAsRead = useCallback((messageId, currentUserId, isGroup = false, groupName = null, otherUserId = null) => {
    // Update local state first
    setMessages(prev =>
      prev.map(msg =>
        msg.id === messageId
          ? { ...msg, is_read: 1 }
          : msg
      )
    );

    // Send API request to mark as read
    const endpoint = isGroup ? '/api/conference-group-chat-mark-read' : '/api/conference-chat-mark-read';
    const payload = isGroup
      ? {
        event_id: eventid,
        group: groupName,
        receiver_id: currentUserId,
      }
      : {
        event_id: eventid,
        sender_id: isGroup ? null : otherUserId,
        receiver_id: currentUserId,
      };

    axios.post(endpoint, payload)
      .then(() => console.log('✅ Message marked as read:', messageId))
      .catch(err => console.error('❌ Failed to mark message as read:', err));
  }, [eventid]);

  // Keep all other existing functions exactly the same
  const handleMessagesRead = useCallback((data) => {
    const currentUserId = parseInt(logedInUser?.id || participantId);
    if (parseInt(data.receiver_id) === currentUserId) {
      setMessages(prev =>
        prev.map(msg =>
          msg.sender_id === data.sender_id && msg.isMine
            ? { ...msg, is_read: 1 }
            : msg
        )
      );
    }
  }, [logedInUser, participantId]);

  const handleGroupMessagesRead = useCallback((data) => {
    if (data.group && data.read_by !== parseInt(logedInUser?.id || participantId)) {
      setGroupUnreadCounts(prev => ({
        ...prev,
        [data.group]: 0
      }));
    }
  }, [logedInUser, participantId]);

  const handleMessageUpdated = useCallback((data) => {
    setMessages(prev =>
      prev.map(msg =>
        msg.id === data.id ? { ...msg, text: data.text } : msg
      )
    );
  }, []);

  const handleMessageDeleted = useCallback((data) => {
    setMessages(prev => prev.filter(msg => msg.id !== data.id));
  }, []);

  const shouldIncrementUnreadCount = useCallback((message) => {
    if (message.isMine) return false;
    if (message.group) {
      return message.group === selectedRole && sendToGroup;
    } else {
      return message.sender_id === (activeChatUser?.id) && !sendToGroup;
    }
  }, [selectedRole, sendToGroup, activeChatUser]);

  const updateUnreadCounts = useCallback((message) => {
    if (message.group) {
      setGroupUnreadCounts(prev => ({
        ...prev,
        [message.group]: (prev[message.group] || 0) + 1
      }));
    } else {
      setUnreadCounts(prev => ({
        ...prev,
        [message.sender_id]: (prev[message.sender_id] || 0) + 1
      }));
    }
  }, []);

  // Fetch initial messages when chat target changes
  useEffect(() => {
    if (eventid && logedInUser && (activeChatUser || (sendToGroup && selectedRole))) {
      fetchInitialMessages();
    }
  }, [eventid, logedInUser, activeChatUser, sendToGroup, selectedRole]);

  const fetchInitialMessages = async () => {
    try {
      const res = await axios.post('/api/conference-chat-fetch', {
        event_id: eventid,
        sender_id: sendToGroup ? null : parseInt(logedInUser?.id || participantId),
        receiver_id: sendToGroup ? null : (activeChatUser?.id ? parseInt(activeChatUser.id) : null),
        group: sendToGroup ? selectedRole : null,
      });

      if (res.data.status === 200) {
        const loadedMessages = res.data.messages.map((msg) => {
          const isMine = parseInt(msg.sender_id) === parseInt(logedInUser?.id || participantId);
          return {
            id: msg.id,
            text: msg.message,
            datetime: msg.datetime || msg.created_at,
            displayTime: formatTime(msg.datetime || msg.created_at),
            sender_id: parseInt(msg.sender_id),
            receiver_id: parseInt(msg.receiver_id),
            group: msg.group,
            isMine,
            is_read: msg.is_read,
            sender_name: msg.sender_name,
            sender_avatar: msg.sender_name?.charAt(0),
            sender_role: msg.sender_role,
          };
        });
        setMessages(loadedMessages);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  };

  // Fetch unread counts (only once on mount)
  useEffect(() => {
    if (!eventid || !logedInUser?.id) return;

    const fetchUnreadCounts = () => {
      axios
        .get(`/api/conference-unread-messages?event_id=${eventid}&receiver_id=${logedInUser.id}`)
        .then(res => {
          const counts = {};
          res.data.data.forEach(item => {
            counts[item.sender_id] = item.unread_count;
          });
          setUnreadCounts(counts);
        })
        .catch(err => {
          console.error("Error fetching unread messages", err);
        });
    };

    const fetchGroupUnreadCounts = () => {
      axios
        .get(`/api/conference-group-unread-messages?event_id=${eventid}&receiver_id=${logedInUser.id}`)
        .then(res => {
          const counts = {};
          res.data.data.forEach(item => {
            counts[item.group] = item.unread_count;
          });
          setGroupUnreadCounts(counts);
        })
        .catch(err => {
          console.error("Error fetching group unread messages", err);
        });
    };

    fetchUnreadCounts();
    fetchGroupUnreadCounts();
  }, [eventid, logedInUser]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allDisplayedMessages]); // Changed to allDisplayedMessages

  // Format time helper
  const formatTime = (datetime) => {
    return new Date(datetime).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // UPDATED: Send message with optimistic update
  const handleSend = async () => {
    if ((!sendToGroup && !activeChatUser) || (sendToGroup && !selectedRole)) return;
    if (newMessage.trim() === '') return;
    setIsSending(true);

    const senderId = parseInt(logedInUser?.id || participantId);
    const receiverId = sendToGroup ? null : parseInt(activeChatUser?.id);
    const nowISO = new Date().toISOString();
    const tempMessageId = `temp-${Date.now()}`; // Temporary ID for optimistic message

    // Create optimistic message
    const optimisticMessage = {
      id: tempMessageId,
      text: newMessage,
      datetime: nowISO,
      displayTime: formatTime(nowISO),
      sender_id: senderId,
      receiver_id: receiverId,
      group: sendToGroup ? selectedRole : null,
      isMine: true,
      is_read: 0,
      sender_name: participantName,
      sender_avatar: participantName?.charAt(0),
      sender_role: participantRole,
      isOptimistic: true, // Flag to identify optimistic messages
    };

    // Add to optimistic messages immediately
    setOptimisticMessages(prev => [...prev, optimisticMessage]);
    setNewMessage(''); // Clear input immediately

    try {
      const res = await axios.post('/api/conference-chat-send', {
        event_id: eventid,
        text: newMessage,
        datetime: nowISO,
        sender_id: senderId,
        receiver_id: receiverId,
        group: sendToGroup ? selectedRole : null,
      });

      if (res.data.status === 200) {
        // Success - the optimistic message will be removed when the real message arrives via WebSocket
        console.log('✅ Message sent successfully');
      } else {
        // If send failed, remove optimistic message and show error
        setOptimisticMessages(prev =>
          prev.filter(msg => msg.id !== tempMessageId)
        );
        console.error('Failed to send message:', res.data.message);
        // You might want to show a toast notification here
      }
    } catch (error) {
      // If error, remove optimistic message
      setOptimisticMessages(prev =>
        prev.filter(msg => msg.id !== tempMessageId)
      );
      console.error('Error sending message:', error);
      // Show error notification to user
    } finally {
      setIsSending(false);
    }
  };

  // Mark as read with WebSocket
  const handleChatUserSelect = async (user) => {
    const numericUserId = parseInt(user.id);
    const currentUserId = parseInt(logedInUser?.id || participantId);

    setActiveChatUser({ ...user, id: numericUserId });
    setSendToGroup(false);

    if (isMobile) {
      setShowUserList(false);
    }

    try {
      await axios.post('/api/conference-chat-mark-read', {
        event_id: eventid,
        sender_id: numericUserId,
        receiver_id: currentUserId,
      });

      setUnreadCounts((prev) => ({
        ...prev,
        [numericUserId]: 0,
      }));

      setMessages(prev =>
        prev.map(msg =>
          msg.sender_id === numericUserId && msg.receiver_id === currentUserId
            ? { ...msg, is_read: 1 }
            : msg
        )
      );

    } catch (error) {
      console.error('Failed to mark messages as read:', error);
    }
  };

  // Group chat selection
  const handleGroupButtonClick = async (role) => {
    setSelectedRole(role);
    setSendToGroup(true);
    setActiveChatUser(null);

    if (isMobile) setShowUserList(false);

    try {
      await axios.post('/api/conference-group-chat-mark-read', {
        event_id: eventid,
        group: role,
        receiver_id: logedInUser?.id || participantId,
      });

      setGroupUnreadCounts((prev) => ({
        ...prev,
        [role]: 0,
      }));
    } catch (error) {
      console.error('Failed to mark group messages as read:', error);
    }
  };

  // Message modification functions - UPDATED FOR OPTIMISTIC MESSAGES
  const canModify = (msg) => {
    if (!msg || !msg.isMine || msg.isOptimistic) return false; // Don't allow modifying optimistic messages
    const diff = (new Date() - new Date(msg.datetime)) / 1000;
    return diff <= 60;
  };

  const handleDeleteClick = (id) => {
    setMessageToDelete(id);
    setDeleteDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`/api/conference-chat-delete/${messageToDelete}`);
    } catch (error) {
      console.error('Error deleting message:', error);
    } finally {
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setMessageToDelete(null);
  };

  const handleEditStart = (id, text) => {
    setEditingMessageId(id);
    setEditingText(text);
    setAnchorEl(null);
  };

  const handleEditSave = async () => {
    if (editingText.trim() === '') return;

    try {
      await axios.put(`/api/conference-chat-update/${editingMessageId}`, {
        message: editingText
      });
      setEditingMessageId(null);
      setEditingText('');
    } catch (error) {
      console.error('Error updating message:', error);
    }
  };

  const handleEditCancel = () => {
    setEditingMessageId(null);
    setEditingText('');
  };

  const handleMessageMenuOpen = (event, message) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleMessageMenuClose = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleBackToUserList = () => {
    setShowUserList(true);
  };

  const toggleUserList = () => {
    setShowUserList(!showUserList);
  };

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(
      (user) =>
        (selectedRole && selectedRole !== "All" ? user.role === selectedRole : true) &&
        user.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, selectedRole, searchTerm]);

  const renderMessageWithLinks = (text) => {
    if (!text) return '';

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (part.match(urlRegex)) {
        const displayText = part.length > 50
          ? part.substring(0, 47) + '...'
          : part;

        return (
          <Tooltip key={index} title={part} arrow>
            <Box
              component="a"
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: muiTheme.palette.primary.main,
                textDecoration: 'underline',
                wordBreak: 'break-all',
                fontWeight: '500',
                display: 'inline',
                '&:hover': {
                  color: muiTheme.palette.primary.dark,
                  textDecoration: 'none',
                },
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {displayText}
            </Box>
          </Tooltip>
        );
      }
      return part;
    });
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Container maxWidth="xl" sx={{ mt: { xs: 8, sm: 12 }, height: 'calc(100vh - 100px)', p: { xs: 1, sm: 2 } }}>
        <Paper
          elevation={8}
          sx={{
            height: '100%',
            display: 'flex',
            overflow: 'hidden',
            position: 'relative',
            borderRadius: 4,
            background: muiTheme.palette.gradient.primary,
          }}
        >
          {/* Theme Controls */}
          <Box sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 1000,
            display: 'flex',
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-end'
          }}>
            <Tooltip title="Connection Status">
              <Chip
                icon={connectionStatus.includes('✅') ? <WifiIcon /> : <WifiOffIcon />}
                label={
                  connectionStatus.includes('✅')
                    ? (isMobile ? 'Live' : 'Live')
                    : (isMobile ? 'Offline' : 'Disconnected')
                }
                size="small"
                sx={{
                  bgcolor: connectionStatus.includes('✅') ? alpha(green[500], 0.9) : alpha(orange[500], 0.9),
                  color: 'white',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(10px)',
                  order: 1,
                }}
              />
            </Tooltip>

            <Box sx={{
              display: 'flex',
              gap: 1,
              order: 2
            }}>
              <Tooltip title="Toggle Theme">
                <IconButton
                  size="small"
                  onClick={handleThemeToggle}
                  sx={{
                    bgcolor: alpha(muiTheme.palette.background.paper, 0.9),
                    color: muiTheme.palette.text.primary,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      bgcolor: alpha(muiTheme.palette.background.paper, 1),
                    },
                  }}
                >
                  {mode === 'light' ? <DarkIcon /> : <LightIcon />}
                </IconButton>
              </Tooltip>

              <Tooltip title="Color Theme">
                <IconButton
                  size="small"
                  onClick={handleColorThemeOpen}
                  sx={{
                    bgcolor: alpha(muiTheme.palette.background.paper, 0.9),
                    color: muiTheme.palette.text.primary,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      bgcolor: alpha(muiTheme.palette.background.paper, 1),
                    },
                  }}
                >
                  <PaletteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Color Theme Menu */}
          <Menu
            anchorEl={themeAnchorEl}
            open={Boolean(themeAnchorEl)}
            onClose={handleColorThemeClose}
            PaperProps={{
              sx: {
                borderRadius: 3,
                p: 1,
                bgcolor: muiTheme.palette.background.paper,
              }
            }}
          >
            <Typography variant="subtitle2" sx={{ p: 1, pb: 0, color: muiTheme.palette.text.secondary }}>
              Color Theme
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, p: 2 }}>
              {Object.entries(colorThemes).map(([key, themeColors]) => (
                <Tooltip key={key} title={key.charAt(0).toUpperCase() + key.slice(1)}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${themeColors.primary[500]}, ${themeColors.secondary[500]})`,
                      cursor: 'pointer',
                      border: colorTheme === key ? `3px solid ${muiTheme.palette.primary.main}` : `2px solid ${muiTheme.palette.divider}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.1)',
                      },
                    }}
                    onClick={() => handleColorThemeChange(key)}
                  />
                </Tooltip>
              ))}
            </Box>
          </Menu>

          {/* Sidebar - No changes needed */}
          <Slide direction="right" in={showUserList} mountOnEnter unmountOnExit>
            <Box
              sx={{
                width: { xs: '100%', sm: 320 },
                display: 'flex',
                flexDirection: 'column',
                background: muiTheme.palette.gradient.sidebar,
                color: 'white',
                position: { xs: 'absolute', sm: 'relative' },
                zIndex: 10,
                height: '100%',
              }}
            >
              {/* User Header */}
              <Box sx={{ p: 3, background: 'rgba(0,0,0,0.2)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: teal[500],
                      border: '3px solid rgba(255,255,255,0.3)',
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {participantName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap fontWeight="bold">
                      {participantName}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {participantRole}
                    </Typography>
                  </Box>
                </Box>

                {/* Chat Type Toggle */}
                <Box sx={{ display: 'flex', gap: 1, background: 'rgba(255,255,255,0.1)', p: 1, borderRadius: 3 }}>
                  <Button
                    fullWidth
                    variant={!sendToGroup ? 'contained' : 'text'}
                    startIcon={<PersonIcon />}
                    onClick={() => setSendToGroup(false)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      bgcolor: !sendToGroup ? 'rgba(255,255,255,0.2)' : 'transparent',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                      },
                    }}
                  >
                    Private
                  </Button>
                  <Button
                    fullWidth
                    variant={sendToGroup ? 'contained' : 'text'}
                    startIcon={<GroupIcon />}
                    onClick={() => setSendToGroup(true)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      bgcolor: sendToGroup ? 'rgba(255,255,255,0.2)' : 'transparent',
                      color: 'white',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                      },
                    }}
                  >
                    Group
                  </Button>
                </Box>
              </Box>

              {/* Group Chats Section */}
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.7, mb: 1, px: 1 }}>
                  GROUP CHATS
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {['All', 'Organizer', 'Speaker', 'Participant'].map((role) => (
                    <Badge
                      key={role}
                      badgeContent={groupUnreadCounts[role] || 0}
                      color="error"
                      sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 0 }}
                    >
                      <Card
                        sx={{
                          background: getRoleGradient(role),
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: 4,
                          },
                        }}
                        onClick={() => handleGroupButtonClick(role)}
                      >
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GroupIcon sx={{ fontSize: 20, opacity: 0.9 }} />
                            <Typography variant="body2" fontWeight="bold" noWrap>
                              {role === 'All' ? 'Everyone' : role}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Badge>
                  ))}
                </Box>
              </Box>

              {/* Search */}
              <Box sx={{ p: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search participants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      borderRadius: 3,
                      background: 'rgba(255,255,255,0.1)',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.5)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'rgba(255,255,255,0.7)',
                      },
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: 'rgba(255,255,255,0.5)',
                    },
                  }}
                />
              </Box>

              {/* Users List */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
                <List disablePadding>
                  {filteredUsers
                    ?.sort((a, b) => {
                      const aUnread = unreadCounts[a.id] || 0;
                      const bUnread = unreadCounts[b.id] || 0;
                      if (aUnread > bUnread) return -1;
                      if (aUnread < bUnread) return 1;
                      if (a.online && !b.online) return -1;
                      if (!a.online && b.online) return 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((user) => (
                      <ListItem key={user.id} disablePadding sx={{ mb: 1 }}>
                        <ListItemButton
                          selected={activeChatUser?.id === user.id}
                          onClick={() => handleChatUserSelect(user)}
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            mx: 1,
                            background: activeChatUser?.id === user.id
                              ? 'rgba(255,255,255,0.2)'
                              : unreadCounts[user.id] > 0 && activeChatUser?.id !== user.id
                                ? 'rgba(255,255,255,0.1)'
                                : 'transparent',
                            border: activeChatUser?.id === user.id ? '2px solid rgba(255,255,255,0.5)' : '2px solid transparent',
                            '&:hover': {
                              background: 'rgba(255,255,255,0.15)',
                            },
                          }}
                        >
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot"
                            sx={{
                              mr: 2,
                              '& .MuiBadge-dot': {
                                backgroundColor: user.online ? '#44b700' : '#ccc',
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                border: '2px solid #2c3e50',
                              }
                            }}
                          >
                            <Avatar
                              sx={{
                                bgcolor: getRoleColor(user.role),
                                width: 44,
                                height: 44,
                                fontSize: '1.1rem',
                                fontWeight: 'bold'
                              }}
                            >
                              {user.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </Badge>

                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography
                                  fontWeight={unreadCounts[user.id] > 0 ? 'bold' : 'normal'}
                                  color={unreadCounts[user.id] > 0 ? 'white' : 'rgba(255,255,255,0.9)'}
                                  noWrap
                                  sx={{ fontSize: '0.95rem' }}
                                >
                                  {user.name}
                                </Typography>
                                {unreadCounts[user.id] > 0 && activeChatUser?.id !== user.id && (
                                  <Badge
                                    badgeContent={unreadCounts[user.id]}
                                    color="error"
                                    sx={{
                                      '& .MuiBadge-badge': {
                                        fontSize: '0.7rem',
                                        height: 20,
                                        minWidth: 20,
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: 'rgba(255,255,255,0.7)',
                                    fontSize: '0.8rem'
                                  }}
                                  noWrap
                                >
                                  {user.role}
                                </Typography>
                                {user.topics && user.topics.length > 0 && (
                                  <Box sx={{ mt: 0.5 }}>
                                    {user.topics.map((topic, index) => (
                                      <React.Fragment key={topic.id}>
                                        <Tooltip title={`${topic.topic} (${topic.start_time} - ${topic.end_time})`} arrow>
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'normal',
                                                color: 'rgba(255,255,255,0.7)',
                                                fontSize: '0.75rem',
                                                cursor: 'help',
                                              }}
                                            >
                                              • {topic.topic}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="text.secondary"
                                              sx={{
                                                fontSize: '0.75rem',
                                                fontStyle: 'italic',
                                                mt: 0.3,
                                                color: 'rgba(255,255,255,0.7)',
                                              }}
                                            >
                                              ({topic.start_time} - {topic.end_time})
                                            </Typography>
                                          </Box>
                                        </Tooltip>
                                        {index !== user.topics.length - 1 && (
                                          <Divider sx={{ my: 0.5, borderColor: 'grey.300' }} />
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </Box>
                                )}
                              </>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </Box>
            </Box>
          </Slide>

          {/* Chat Area */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
              background: muiTheme.palette.background.default,
              position: 'relative',
            }}
          >
            {/* Chat Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${muiTheme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                background: muiTheme.palette.background.paper,
              }}
            >
              {isMobile && (
                <IconButton
                  onClick={toggleUserList}
                  sx={{
                    background: muiTheme.palette.gradient.primary,
                    color: 'white',
                    '&:hover': {
                      background: muiTheme.palette.gradient.secondary,
                    },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              {activeChatUser ? (
                <>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                    sx={{
                      '& .MuiBadge-dot': {
                        backgroundColor: activeChatUser.online ? '#44b700' : '#ccc',
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        border: `2px solid ${muiTheme.palette.background.paper}`,
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: getRoleColor(activeChatUser.role),
                        width: 48,
                        height: 48,
                      }}
                    >
                      {activeChatUser.name.charAt(0).toUpperCase()}
                    </Avatar>
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap fontWeight="bold" color={muiTheme.palette.text.primary}>
                      {activeChatUser.name}
                    </Typography>
                    <Typography variant="body2" color={muiTheme.palette.text.secondary} noWrap>
                      {activeChatUser.role} • {activeChatUser.online ? 'Online' : 'Offline'}
                    </Typography>
                  </Box>
                </>
              ) : sendToGroup && selectedRole ? (
                <>
                  <Avatar
                    sx={{
                      bgcolor: getRoleColor(selectedRole),
                      width: 48,
                      height: 48,
                    }}
                  >
                    {selectedRole === "All" ? "A" : selectedRole.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap fontWeight="bold" color={muiTheme.palette.text.primary}>
                      {selectedRole === "All" ? "Everyone" : `${selectedRole}s Group`}
                    </Typography>
                    <Typography variant="body2" color={muiTheme.palette.text.secondary} noWrap>
                      Group chat • {users.filter(u => u.role === selectedRole || selectedRole === 'All').length} participants
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ flex: 1, textAlign: 'center' }}>
                  <Typography variant="h6" color={muiTheme.palette.text.secondary}>
                    Welcome to {eventName} Chat
                  </Typography>
                  <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                    Select a conversation to start messaging
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Messages Area - UPDATED TO USE allDisplayedMessages */}
            {/* Messages Area - SIMPLIFIED */}
            {/* Messages Area - UPDATED TO USE allDisplayedMessages */}
{/* Messages Area - SIMPLIFIED */}
<Box
  ref={messagesContainerRef}
  sx={{
    flex: 1,
    p: 2,
    overflowY: 'auto',
    background: mode === 'light'
      ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      : 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    position: 'relative',
  }}
>
  {allDisplayedMessages.map((msg, index) => {
    const isEditing = editingMessageId === msg.id;
    const isOptimistic = msg.isOptimistic;
    const senderAvatar = msg.isMine
      ? participantName.charAt(0).toUpperCase()
      : msg.sender_avatar?.toUpperCase();

    return (
      <Fade in={true} key={msg.id}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: msg.isMine ? 'flex-end' : 'flex-start',
            mb: 2,
          }}
        >
          {!msg.isMine && (
            <Avatar
              sx={{
                bgcolor: getRoleColor(msg.sender_role),
                mr: 1,
                alignSelf: 'flex-end',
                width: 36,
                height: 36,
              }}
            >
              {senderAvatar}
            </Avatar>
          )}
          <Box sx={{ maxWidth: { xs: '85%', sm: '70%' } }}>
            {isEditing ? (
              <Card sx={{ p: 2, background: muiTheme.palette.background.paper, boxShadow: 3 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={editingText}
                  onChange={(e) => setEditingText(e.target.value)}
                  multiline
                  maxRows={4}
                  autoFocus
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CloseIcon />}
                    onClick={handleEditCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleEditSave}
                    disabled={editingText.trim() === ''}
                  >
                    Save
                  </Button>
                </Box>
              </Card>
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: muiTheme.palette.background.paper,
                  color: muiTheme.palette.text.primary,
                  p: 2,
                  borderRadius: '20px 20px 20px 5px',
                  boxShadow: 2,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  border: msg.isMine
                    ? `2px solid ${muiTheme.palette.primary.main}`
                    : `2px solid ${muiTheme.palette.divider}`,
                }}
              >
                {/* Show loading indicator for optimistic messages */}
                {/* {isOptimistic && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -5,
                      right: -5,
                    }}
                  >
                    <CircularProgress size={16} />
                  </Box>
                )} */}

                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    fontSize: '0.95rem',
                    mb: 1,
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {renderMessageWithLinks(msg.text)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                    fontSize: '0.75rem',
                    color: msg.isMine ? 'rgba(255,255,255,0.8)' : muiTheme.palette.text.secondary,
                  }}
                >
                  {/* <Typography variant="caption">
                    {msg.displayTime} {isOptimistic && '(Sending...)'}
                  </Typography> */}
                    <Typography variant="caption">
                    {msg.displayTime}
                  </Typography>
                  {msg.isMine && !isOptimistic && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: msg.is_read ? alpha(green[500], 0.8) : 'rgba(255,255,255,0.6)',
                      }}
                    >
                      {msg.is_read ? '✓✓' : '✓'}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Fade>
    );
  })}
  <div ref={chatEndRef} />
</Box>

            {/* Message Input - No changes needed */}
            <Box
              sx={{
                p: 2,
                borderTop: `1px solid ${muiTheme.palette.divider}`,
                background: muiTheme.palette.background.paper,
              }}
            >
              {sendToGroup && logedInUser?.cpjoin_as?.join_as !== "Organizer" ? (
                <Card sx={{ p: 3, textAlign: 'center', background: alpha(orange[50], 0.5) }}>
                  <Typography variant="body1" color={muiTheme.palette.text.secondary} gutterBottom>
                    <GroupIcon sx={{ fontSize: 40, mb: 1, color: orange[300] }} />
                  </Typography>
                  <Typography variant="body1" color={muiTheme.palette.text.secondary}>
                    Only Organizers can send group messages.
                  </Typography>
                  <Typography variant="body2" color={muiTheme.palette.text.secondary}>
                    Please select a user for private chat.
                  </Typography>
                </Card>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={
                      sendToGroup
                        ? selectedRole
                          ? selectedRole === "All"
                            ? "Message to Everyone..."
                            : `Message to ${selectedRole}s...`
                          : 'Select role for group message'
                        : activeChatUser
                          ? `Message ${activeChatUser.name}...`
                          : 'Select a chat to start messaging...'
                    }
                    disabled={sendToGroup && !selectedRole}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    multiline
                    maxRows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        background: muiTheme.palette.background.default,
                      },
                    }}
                  />

                  {/* Show PersonIcon FAB when message is empty, otherwise show Send FAB */}
                  {newMessage.trim() === '' && isMobile && !showUserList ? (
                    <Fab
                      color="primary"
                      sx={{
                        background: muiTheme.palette.gradient.primary,
                        '&:hover': {
                          background: muiTheme.palette.gradient.secondary,
                        },
                      }}
                      onClick={toggleUserList}
                    >
                      <PersonIcon />
                    </Fab>
                  ) : (
                    <Fab
                      color="primary"
                      onClick={handleSend}
                      disabled={
                        isSending ||
                        (sendToGroup && !selectedRole) ||
                        (!sendToGroup && !activeChatUser)
                      }
                      sx={{
                        background: isSending
                          ? muiTheme.palette.grey[400]
                          : muiTheme.palette.gradient.primary,
                        '&:hover': {
                          background: isSending
                            ? muiTheme.palette.grey[400]
                            : muiTheme.palette.gradient.secondary,
                        },
                        transform: isSending ? 'scale(0.95)' : 'scale(1)',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {isSending ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                      ) : (
                        <SendIcon />
                      )}
                    </Fab>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3, mb: 2, pt: 2, borderTop: `1px solid ${muiTheme.palette.divider}` }}>
              <Typography
                variant="caption"
                color={muiTheme.palette.text.secondary}
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  opacity: 0.7
                }}
              >
                For any support, contact:
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                <Typography
                  variant="caption"
                  component="a"
                  href="https://microartonline.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: muiTheme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    fontSize: '0.75rem'
                  }}
                >
                  www.microartonline.com
                </Typography>
                <Typography
                  variant="caption"
                  component="a"
                  href="https://wa.me/+966508294744"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: green[500],
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <span>WhatsApp: +966508294744</span>
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            PaperProps={{
              sx: { borderRadius: 3, background: muiTheme.palette.background.paper }
            }}
          >
            <DialogTitle sx={{ background: muiTheme.palette.gradient.primary, color: 'white' }}>
              Delete Message
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Typography color={muiTheme.palette.text.primary}>
                Are you sure you want to delete this message? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={handleDeleteCancel} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Message Actions Popover */}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleMessageMenuClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: { borderRadius: 2, overflow: 'hidden', background: muiTheme.palette.background.paper }
            }}
          >
            <Box sx={{ p: 1 }}>
              <Button
                fullWidth
                startIcon={<EditIcon />}
                onClick={() => {
                  handleEditStart(selectedMessage.id, selectedMessage.text);
                  handleMessageMenuClose();
                }}
                disabled={!canModify(selectedMessage)}
                sx={{ justifyContent: 'flex-start', color: muiTheme.palette.text.primary }}
              >
                Edit
              </Button>
              <Divider sx={{ my: 1 }} />
              <Button
                fullWidth
                startIcon={<DeleteIcon />}
                onClick={() => {
                  handleDeleteClick(selectedMessage.id);
                  handleMessageMenuClose();
                }}
                disabled={!canModify(selectedMessage)}
                color="error"
                sx={{ justifyContent: 'flex-start' }}
              >
                Delete
              </Button>
            </Box>
          </Popover>
        </Paper>
      </Container>
    </ThemeProvider >
  );
};

export default ParticipantSingleEventDashboard;